import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { BurgerMenu as BurgerMenuIcon } from '@ori-ui/icons';
import { Backdrop, Box, CircularProgress, Modal, SHADOW_LEVEL1, Slide, styled, useTheme } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { lazy, Suspense, useCallback, useEffect } from 'react';

import { CSS_DESKTOP_INNER_HEIGHT, CSS_HEIGHT_ANIMATION, DESKTOP_HEIGHT } from '../../constants';
import { useAppConfig } from '../../context';
import { useActiveMenu, usePbsContent } from '../../context/navigation';
import { useIsMobile, useCustomerInitials, useLazyPersonalMenu } from '../../hooks';
import { useTranslations } from '../../hooks/useTranslations';
import { BurgerMenuTabNames } from '../../models';
import { BurgerMenuContent } from './BurgerMenuContent';
import { BurgerMenuLinks } from './BurgerMenuLinks/BurgerMenuLinks';
import { BurgerMenuTab } from './BurgerMenuTab';
import { BURGER_MENU_BACKDROP, BURGER_MENU_CONTAINER, BURGER_MENU_ROOT } from './constants';

const HEIGHT_BUFFER = 20;

const BurgerMenuRoot = styled('aside')<{ ownerState: { isMobile?: boolean; isDesktop?: boolean } }>(
  ({ ownerState, theme }) => ({
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[100]}`,
    display: 'none',
    transition: `top ${CSS_HEIGHT_ANIMATION}`,

    ...(ownerState.isMobile && {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      maxWidth: 'calc(100% - 45px)',
      position: 'absolute',
      width: 330,
    }),

    ...(ownerState.isDesktop && {
      boxShadow: SHADOW_LEVEL1,
      display: 'flex',
      left: 0,
      minHeight: 590,
      position: 'absolute',
      top: CSS_DESKTOP_INNER_HEIGHT,
      width: '100%',
    }),
  }),
);

const BurgerMenuInner = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    width: 'inherit',
    maxHeight: `calc(100vh - ${DESKTOP_HEIGHT}px - ${HEIGHT_BUFFER}px)`,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
}));

const BurgerMenuContainer = styled('div')(() => ({
  display: 'flex',
  height: 54,
  width: '100%',
}));

// istanbul ignore next - lazy loading is not supported by Jest
const LazyAccount = lazy(async () => import('../Account/LoginLibrary')) as FC;

const LazyBurgerMenuLinks = lazy(async () => import('./BurgerMenuLinks/BurgerMenuLinks')) as FC;

const fallback = (
  <Box
    display="flex"
    justifyContent="center"
  >
    <CircularProgress />
  </Box>
);

export const BurgerMenu: FC = () => {
  const { BurgerMenu: BurgerMenuType, AccountSideMenu: AccountSideMenuType } = ToggleMenuFlyout.MenuTypes;
  const { useSsoIdentityLogin } = useAppConfig();
  const { activeMenu, toggleActiveMenu } = useActiveMenu();
  const { isShown, setIsShown } = usePbsContent();
  const isMobile = useIsMobile();
  const ownerState = { isMobile };
  const handleTabClick = useCallback(
    (tabName: BurgerMenuTabNames) => {
      const menuType: ToggleMenuFlyout.MenuTypes =
        tabName === BurgerMenuTabNames.ProductsTab ? BurgerMenuType : AccountSideMenuType;
      toggleActiveMenu(menuType);
    },
    [AccountSideMenuType, BurgerMenuType, toggleActiveMenu],
  );
  const { translations } = useTranslations();
  const { zIndex } = useTheme();
  const customerInitials = useCustomerInitials();
  const { isAnonymousUser } = useToken();
  const isLoggedIn = !isAnonymousUser;
  const isMenuActive = activeMenu === BurgerMenuType || (isMobile && activeMenu === AccountSideMenuType);
  const activeTab = activeMenu === BurgerMenuType ? BurgerMenuTabNames.ProductsTab : BurgerMenuTabNames.AccountTab;
  const { getTestId } = useTestId();
  const personalMenu = useLazyPersonalMenu();

  useEffect(() => {
    if (isShown && activeMenu === '') {
      setIsShown(true);
    }
  }, [activeMenu, isShown, setIsShown]);

  const accountTabContent = isAnonymousUser ? <LazyAccount /> : personalMenu;

  const onClickOverlay = useCallback(() => {
    toggleActiveMenu(BurgerMenuType);
  }, [BurgerMenuType, toggleActiveMenu]);

  if (isMobile) {
    return (
      <Modal
        style={{ zIndex: zIndex.drawer }}
        componentsProps={{ backdrop: { onClick: onClickOverlay } }}
        open={isMenuActive}
      >
        <Slide
          in={isMenuActive}
          direction="right"
        >
          <BurgerMenuRoot
            ownerState={ownerState}
            data-testid={getTestId(BURGER_MENU_ROOT)}
          >
            <BurgerMenuContainer data-testid={getTestId(BURGER_MENU_CONTAINER)}>
              <BurgerMenuTab
                activeTab={activeTab}
                tabName={BurgerMenuTabNames.ProductsTab}
                tabPrefix={BurgerMenuIcon}
                onTabClick={handleTabClick}
              >
                {translations.products}
              </BurgerMenuTab>
              {!isLoggedIn && useSsoIdentityLogin ? null : (
                <BurgerMenuTab
                  activeTab={activeTab}
                  tabName={BurgerMenuTabNames.AccountTab}
                  tabPrefix={customerInitials ?? ''}
                  isLoggedIn={isLoggedIn}
                  onTabClick={handleTabClick}
                >
                  {isLoggedIn ? translations.myAccount : `${translations.logIn}/${translations.register}`}
                </BurgerMenuTab>
              )}
            </BurgerMenuContainer>
            <Suspense fallback={fallback}>
              {activeTab === BurgerMenuTabNames.ProductsTab && <LazyBurgerMenuLinks />}
              {activeTab === BurgerMenuTabNames.AccountTab && accountTabContent}
            </Suspense>
          </BurgerMenuRoot>
        </Slide>
      </Modal>
    );
  }

  if (!isMenuActive) {
    return null;
  }

  return (
    <BurgerMenuRoot
      as="div"
      ownerState={{ ...ownerState, isDesktop: true }}
      data-testid={getTestId(BURGER_MENU_ROOT)}
    >
      <BurgerMenuInner>
        <BurgerMenuLinks />
        <BurgerMenuContent />
        <Backdrop
          open
          data-testid={getTestId(BURGER_MENU_BACKDROP)}
          sx={{ position: 'absolute', zIndex: '-1', width: '100vw', height: '100vh' }}
          onClick={onClickOverlay}
        />
      </BurgerMenuInner>
    </BurgerMenuRoot>
  );
};
